import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { useScope } from "@/context/ScopeContext";
import {
  Upload,
  FileSpreadsheet,
  Check,
  School,
  MapPin,
  Link2,
  AlertTriangle,
  BarChart3,
  Shield,
  Calendar,
  Droplets,
  Users,
  Building2,
  Pencil,
  MessageSquare,
  GraduationCap,
  Download,
  LineChart,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { getBackendUrl } from "@/lib/backend";
import { useMistralNarrativeOverlay } from "@/hooks/useMistralNarrativeOverlay";
import {
  buildDatasetCatalog,
  buildTemplateRows,
  buildImportKpis,
  buildReadinessState,
  buildDataImportMarkdown,
  filterImports,
  mapImportRow,
  getStatusTagClass,
} from "@/utils/dataImportDerived";
import "./DataImport.css";
import { toTitleCase } from "../utils/toTitleCase";

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const DATASET_ICONS = {
  aadhaar: Shield,
  apaar: Calendar,
  water: Droplets,
  enrolment: Users,
  classroom: Building2,
  data_entry: BarChart3,
  comparison: Pencil,
  remarks: MessageSquare,
  age: Users,
  teacher: GraduationCap,
};

const HISTORY_FILTERS = [
  { id: "all", label: "All" },
  { id: "successful", label: "Successful" },
  { id: "warnings", label: "Warnings" },
  { id: "failed", label: "Failed" },
];

const DataImport = () => {
  const { scope } = useScope();
  const fileInputRef = useRef(null);
  const templateRef = useRef(null);

  const [imports, setImports] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const [importingUrl, setImportingUrl] = useState(false);
  const [historyFilter, setHistoryFilter] = useState("all");
  const [dragOver, setDragOver] = useState(false);
  const [fileHint, setFileHint] = useState(
    "Maximum recommended size: 25 MB · Supported formats: .xlsx, .xls"
  );

  const fetchData = useCallback(async () => {
    try {
      const [importsRes, statsRes] = await Promise.all([
        axios.get(`${API}/import/list`),
        axios.get(`${API}/import/stats`),
      ]);
      setImports(Array.isArray(importsRes.data) ? importsRes.data : []);
      setStats(statsRes.data);
    } catch (error) {
      const status = error?.response?.status;
      if (status !== 404) {
        console.error("Error fetching import data:", error);
      }
      setImports([]);
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [scope.version]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const kpis = useMemo(() => buildImportKpis(stats), [stats]);
  const localReadiness = useMemo(
    () => buildReadinessState(imports, uploading, importingUrl),
    [imports, uploading, importingUrl]
  );
  const buildLocalMarkdown = useCallback(
    () => buildDataImportMarkdown(imports, uploading, importingUrl),
    [imports, uploading, importingUrl]
  );
  const scopeKey = useMemo(
    () => JSON.stringify({ n: imports.length, u: uploading, i: importingUrl }),
    [imports.length, uploading, importingUrl]
  );
  const { loading: aiLoading, pickHeadline, pickFromBucket, isMistral } = useMistralNarrativeOverlay({
    kind: "data-import",
    title: "Data Import Readiness",
    buildLocalMarkdown,
    context: { imports, uploading, importingUrl },
    enabled: !loading,
    scopeKey,
  });
  const readiness = useMemo(
    () => ({
      ...localReadiness,
      headline: aiLoading
        ? localReadiness.headline
        : pickHeadline(localReadiness.headline),
      narrative: aiLoading
        ? localReadiness.narrative
        : pickFromBucket(localReadiness.narrative, "insights", 0),
      aiLabel: aiLoading ? "Generating…" : isMistral ? "Mistral AI" : "Rule-based",
    }),
    [localReadiness, aiLoading, pickHeadline, pickFromBucket, isMistral]
  );
  const filteredImports = useMemo(
    () => filterImports(imports, historyFilter).map(mapImportRow),
    [imports, historyFilter]
  );
  const datasetCatalog = useMemo(() => buildDatasetCatalog(imports), [imports]);
  const templateRows = useMemo(() => buildTemplateRows(imports, stats), [imports, stats]);

  const uploadFile = async (file) => {
    if (!file) return;
    if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
      toast.error("Please upload an Excel file (.xlsx or .xls)");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      await axios.post(`${API}/import/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`File "${file.name}" uploaded successfully. Processing started.`);
      setFileHint(`Selected: ${file.name} · Ready for validation preview`);
      fetchData();
    } catch (error) {
      toast.error(`Upload failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    await uploadFile(file);
    event.target.value = "";
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    setDragOver(false);
    const file = event.dataTransfer.files?.[0];
    await uploadFile(file);
  };

  const handleUrlImport = async () => {
    if (!urlInput.trim()) {
      toast.error("Please enter a URL");
      return;
    }

    setImportingUrl(true);
    try {
      const response = await axios.post(
        `${API}/import/from-url?url=${encodeURIComponent(urlInput)}`
      );
      toast.success(`Import started for: ${response.data.filename || "remote file"}`);
      setUrlInput("");
      fetchData();
    } catch (error) {
      toast.error(`Import failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setImportingUrl(false);
    }
  };

  const triggerFilePicker = () => fileInputRef.current?.click();

  const scrollToTemplate = () => {
    templateRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleExportLog = () => {
    if (!imports.length) {
      toast.info("No import history to export yet.");
      return;
    }
    toast.success("Export log will be available after the first validated import.");
  };

  const handleDownloadTemplate = () => {
    toast.info("Download the approved Excel template from your district data team.");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div className="data-import-page animate-fade-in" data-testid="data-import" id="di-overview">
      <section className="di-page-header">
        <div>
          <div className="di-eyebrow">
            <LineChart size={18} />
            Admin workflow · Validated imports · Audit-ready
          </div>
          <h1>Data Import Control Center</h1>
          <p>
            Upload, validate, map and publish school datasets with a clearer import journey,
            stronger quality gates and a complete audit trail for every dataset refresh.
          </p>
        </div>
        <div className="di-header-actions">
          <button type="button" className="di-secondary-action" onClick={handleDownloadTemplate}>
            <Download size={18} />
            Download Template
          </button>
          <button
            type="button"
            className="di-primary-action"
            onClick={triggerFilePicker}
            disabled={uploading}
            data-testid="start-import-btn"
          >
            <Upload size={18} />
            {uploading ? "Uploading..." : "Start Import"}
          </button>
          <button type="button" className="di-secondary-action" onClick={fetchData} data-testid="refresh-btn">
            <RefreshCw size={18} />
            Refresh
          </button>
        </div>
      </section>

      <section className="di-metric-grid" aria-label="Import KPI summary">
        {kpis.map((card) => (
          <article key={card.label} className="di-metric-card">
            <div className="di-metric-top">
              <small>{card.label}</small>
              {card.statusPill ? (
                <span className={`di-status-pill ${card.statusClass}`.trim()}>
                  <i className="di-status-dot" />
                  {card.statusPill}
                </span>
              ) : (
                <span className={`di-icon-box ${card.iconClass}`.trim()}>
                  {card.iconClass === "green" ? (
                    <Check size={18} />
                  ) : card.iconClass === "purple" ? (
                    <MapPin size={18} />
                  ) : (
                    <School size={18} />
                  )}
                </span>
              )}
            </div>
            <strong>{card.value}</strong>
            <span className="note">{card.note}</span>
          </article>
        ))}
      </section>

      <section className="di-import-hero" aria-label="Import workbench">
        <div className="di-hero-layout">
          <article className="di-panel">
            <div className="di-panel-head">
              <div>
                <h2>Import School Data</h2>
                <p>
                  One primary upload area with URL import kept as a secondary path to reduce
                  confusion.
                </p>
              </div>
              <span className="di-pill">.xlsx · .xls</span>
            </div>

            <div
              className={`di-drop-zone ${dragOver ? "drag-over" : ""}`}
              onClick={triggerFilePicker}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && triggerFilePicker()}
            >
              <input
                ref={fileInputRef}
                className="di-hidden-input"
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                disabled={uploading}
                data-testid="file-upload-input"
              />
              <div>
                <div className="di-upload-icon">
                  <FileSpreadsheet size={24} />
                </div>
                <h3>{uploading ? "Uploading file..." : "Drop Excel file here"}</h3>
                <p>
                  Use the approved template for faster mapping. The system will preview columns,
                  flag missing values and stop duplicate records before publishing.
                </p>
                <div className="di-upload-actions">
                  <button
                    type="button"
                    className="di-primary-action"
                    onClick={(e) => {
                      e.stopPropagation();
                      triggerFilePicker();
                    }}
                    disabled={uploading}
                  >
                    Choose File
                  </button>
                  <button
                    type="button"
                    className="di-secondary-action"
                    onClick={(e) => {
                      e.stopPropagation();
                      scrollToTemplate();
                    }}
                  >
                    View Template Rules
                  </button>
                </div>
                <div className="di-file-hint">{fileHint}</div>
              </div>
            </div>

            <div className="di-url-import" aria-label="Import from URL">
              <label>
                <Link2 size={18} />
                <input
                  placeholder="Paste public Excel URL..."
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  disabled={importingUrl}
                  data-testid="url-input"
                />
              </label>
              <button
                type="button"
                className="di-secondary-action"
                onClick={handleUrlImport}
                disabled={importingUrl || !urlInput.trim()}
                data-testid="import-url-btn"
              >
                {importingUrl ? "Importing..." : "Import from URL"}
              </button>
            </div>

            <div className="di-mini-checks">
              <span className="di-mini-check">
                <Check size={18} />
                Column validation
              </span>
              <span className="di-mini-check">
                <Check size={18} />
                Duplicate check
              </span>
              <span className="di-mini-check">
                <Check size={18} />
                Audit log
              </span>
            </div>
          </article>

          <article className="di-panel di-readiness-card">
            <div className="di-panel-head">
              <div>
                <h3>Import Readiness</h3>
                <p>
                  Clear next steps before data goes into production dashboards.{" "}
                  <span className="di-ai-pill">{readiness.aiLabel}</span>
                </p>
              </div>
              <span className={`di-status-pill ${readiness.statusClass}`.trim()}>
                <i className="di-status-dot" />
                {readiness.statusPill}
              </span>
            </div>

            <div className="di-readiness-score">
              <div className="di-ring" style={readiness.ringStyle} aria-label={`${readiness.pct}% ready`}>
                <div>
                  <strong>{readiness.pct}%</strong>
                  <span>Ready</span>
                </div>
              </div>
              <div>
                <h3>{toTitleCase(readiness.headline)}</h3>
                <p>{readiness.narrative}</p>
              </div>
            </div>

            <div className="di-step-list">
              {readiness.steps.map((step, index) => (
                <div key={step.title} className={`di-step ${step.active ? "active" : ""}`.trim()}>
                  <div className="di-step-num">{index + 1}</div>
                  <div>
                    <b>{step.title}</b>
                    <span>{step.description}</span>
                  </div>
                  <em>{step.status}</em>
                </div>
              ))}
            </div>

            <div className="di-quality-note">
              <span className="di-icon-box orange">
                <AlertTriangle size={18} />
              </span>
              <div>
                <b>Quality gate enabled</b>
                <span>
                  Incomplete district, block, school code or duplicate records should be reviewed
                  before final publishing.
                </span>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section className="di-two-column">
        <article className="di-panel">
          <div className="di-section-title">
            <div>
              <h2>Supported Dataset Types</h2>
              <p>
                Dataset cards are grouped visually so admin users can quickly identify what they
                are importing.
              </p>
            </div>
            <span className="di-pill">{datasetCatalog.length} datasets</span>
          </div>
          <div className="di-domain-grid">
            {datasetCatalog.map((item) => {
              const Icon = DATASET_ICONS[item.type] || FileSpreadsheet;
              return (
                <div key={item.type} className="di-domain-card">
                  <span className={`di-icon-box ${item.iconClass}`.trim()}>
                    <Icon size={18} />
                  </span>
                  <div>
                    <h4>{item.label}</h4>
                    <p>{item.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </article>

        <article className="di-panel" ref={templateRef} id="di-template">
          <div className="di-section-title">
            <div>
              <h2>Template Readiness</h2>
              <p>Make the next action obvious before the admin starts uploading.</p>
            </div>
            <span className="di-pill">Quality rules</span>
          </div>
          <div className="di-template-grid">
            {templateRows.map((row) => (
              <div key={row.title} className="di-template-row">
                <div>
                  <b>{row.title}</b>
                  <span>{row.description}</span>
                </div>
                <span className={`di-tag ${row.tagClass}`.trim()}>{row.tag}</span>
              </div>
            ))}
          </div>
          <div className="di-audit-cards">
            <div className="di-audit-card">
              <small>Rejected rows</small>
              <b>{stats?.rejected_rows ?? 0}</b>
            </div>
            <div className="di-audit-card">
              <small>Duplicate rows</small>
              <b>{stats?.duplicate_rows ?? 0}</b>
            </div>
            <div className="di-audit-card">
              <small>Missing values</small>
              <b>{stats?.missing_values ?? 0}</b>
            </div>
          </div>
        </article>
      </section>

      <section className="di-panel di-history-panel">
        <div className="di-section-title">
          <div>
            <h2>Import History & Audit Trail</h2>
            <p>
              Recent uploads, validation outcomes and publishing status will appear here after the
              first import.
            </p>
          </div>
          <button type="button" className="di-secondary-action" onClick={handleExportLog}>
            <Download size={18} />
            Export Log
          </button>
        </div>

        <div className="di-history-toolbar">
          <div className="di-segmented" aria-label="History filter">
            {HISTORY_FILTERS.map((filter) => (
              <button
                key={filter.id}
                type="button"
                className={`di-segment ${historyFilter === filter.id ? "active" : ""}`}
                onClick={() => setHistoryFilter(filter.id)}
              >
                {filter.label}
              </button>
            ))}
          </div>
          <span className="di-pill">
            {imports.length ? `${filteredImports.length} records` : "No imports yet"}
          </span>
        </div>

        <div className="di-table-shell">
          <div className="di-table-wrap">
            <table aria-label="Import history table">
              <thead>
                <tr>
                  <th>Dataset</th>
                  <th>Source</th>
                  <th>Rows</th>
                  <th>Validation</th>
                  <th>Imported By</th>
                  <th>Time</th>
                  <th>Status</th>
                </tr>
              </thead>
              {filteredImports.length > 0 && (
                <tbody>
                  {filteredImports.map((row) => (
                    <tr key={row.id} data-testid={`import-item-${row.id}`}>
                      <td>{row.dataset}</td>
                      <td>{row.source}</td>
                      <td>{row.rows}</td>
                      <td>{row.validation}</td>
                      <td>{row.importedBy}</td>
                      <td>{row.time}</td>
                      <td>
                        <span className={`di-tag ${getStatusTagClass(row.status)}`.trim()}>
                          {row.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              )}
            </table>
          </div>

          {filteredImports.length === 0 && (
            <div className="di-empty-state">
              <div>
                <div className="di-upload-icon">
                  <FileSpreadsheet size={24} />
                </div>
                <h3>No Import History Available</h3>
                <p>
                  Upload a validated Excel file to create the first audit entry. Each import will
                  capture dataset type, row count, validation issues, user and timestamp.
                </p>
                <button type="button" className="di-primary-action" onClick={triggerFilePicker}>
                  Upload First Dataset
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default DataImport;
